import React, { ReactNode, useEffect, useRef } from 'react'
import type { CohortRow } from '../../types'
import { formatUpdated } from '../../utils'
import styles from './aboutPanel.module.sass'

type Props = {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}

// A native modal dialog drawn as a right-hand drawer: Escape, focus trapping,
// returning focus to the button that opened it and the dimmed page come with
// <dialog>. Opening another card's panel swaps the content in place.
export const AboutPanel = ({ open, title, onClose, children }: Props) => {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  // A click on the dimmed page lands on the dialog element itself.
  const onClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) onClose()
  }

  return (
    <dialog ref={dialogRef} className={`ffpDashboard ${styles.panel}`} aria-label={title} onClose={onClose} onClick={onClick}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>About this data</p>
            <h2 className={styles.title}>{title}</h2>
          </div>
          <button type="button" className={styles.close} onClick={onClose}>Close</button>
        </header>
        <div className={styles.body}>{children}</div>
      </div>
    </dialog>
  )
}

export const PanelSection = ({ heading, children }: { heading: string; children: ReactNode }) => (
  <section className={styles.section}>
    <h3 className={styles.sectionHeading}>{heading}</h3>
    {children}
  </section>
)

// A paragraph: the definition (default) or the smaller grey working under it.
export const PanelText = ({ muted = false, children }: { muted?: boolean; children: ReactNode }) => (
  <p className={muted ? styles.textMuted : styles.text}>{children}</p>
)

// What a measure is, and the working behind it.
export const PanelMeaning = ({ definition, working }: { definition: string; working: string }) => (
  <PanelSection heading="What it means">
    <PanelText>{definition}</PanelText>
    <PanelText muted>{working}</PanelText>
  </PanelSection>
)

export const PanelNoBenchmark = () => (
  <PanelSection heading="Benchmark">
    <PanelText>There is no outside benchmark for this measure.</PanelText>
  </PanelSection>
)

// The small print under a table or a list of sources.
export const PanelNote = ({ children }: { children: ReactNode }) => (
  <p className={styles.note}>{children}</p>
)

const ANONYMITY_NOTE = 'Any month or group with fewer than 5 people is hidden to protect anonymity.'
const COHORT_NOTE = 'Cohorts with fewer than 5 people are hidden. When a smaller group could be worked out from the others, every cohort is hidden. A cohort with nobody onboarded yet is still listed.'

type PanelTableProps = {
  columns: string[]
  rows: (string | number)[][]
  // Defaults to the anonymity line every numbers table carries.
  note?: string
}

export const PanelTable = ({ columns, rows, note = ANONYMITY_NOTE }: PanelTableProps) => (
  <div className={styles.tableBlock}>
    <div className={styles.tableFrame}>
      <table className={styles.table}>
        <thead className={styles.tableHead}>
          <tr>
            <HeaderCells columns={columns} />
          </tr>
        </thead>
        <tbody>
          <BodyRows rows={rows} />
        </tbody>
      </table>
    </div>
    <PanelNote>{note}</PanelNote>
  </div>
)

const HeaderCells = ({ columns }: { columns: string[] }) => {
  const cells = columns.map((column, index) => (
    <th key={index} scope="col" className={styles.th}>{column}</th>
  ))
  return <>{cells}</>
}

const BodyRows = ({ rows }: { rows: (string | number)[][] }) => {
  const body = rows.map((row, index) => (
    <tr key={index}>
      <BodyCells row={row} />
    </tr>
  ))
  return <>{body}</>
}

const BodyCells = ({ row }: { row: (string | number)[] }) => {
  const cells = row.map((cell, index) => (
    <td key={index} className={styles.td}>{cell}</td>
  ))
  return <>{cells}</>
}

type CohortTableProps = {
  rows: CohortRow[]
  notInCohort: number
  valueHeading: string
  formatValue: (value: number) => string
}

const cohortCells = (row: CohortRow, formatValue: (value: number) => string): string[] => {
  // Cohorts can be saved without a name.
  const name = row.name || 'Unnamed cohort'
  if (row.state === 'nobody_onboarded') return [name, 'Nobody onboarded yet', '0']
  if (row.state === 'withheld') return [name, 'Hidden: protects a smaller group', 'Hidden']
  // A hidden cohort's count is withheld too: it could point at someone.
  const people = row.people === null ? 'Fewer than 5' : String(row.people)
  if (row.state === 'hidden' || row.value === null) return [name, 'Hidden: fewer than 5', people]
  return [name, formatValue(row.value), people]
}

export const CohortTable = ({ rows, notInCohort, valueHeading, formatValue }: CohortTableProps) => {
  const tableRows = rows
    .map((row) => cohortCells(row, formatValue))
    .concat([['Not in a cohort', '', String(notInCohort)]])

  return <PanelTable columns={['Cohort', valueHeading, 'People counted']} rows={tableRows} note={COHORT_NOTE} />
}

// "Coaching: from coaching bookings, last updated Mon 21 Sep". Name the kind
// of record, never the tool it comes from. Without a time, just the source.
export const PanelFreshness = ({ source, at, label = 'last updated' }: { source: string; at: string | null; label?: string }) => {
  const updated = formatUpdated(at)
  const text = updated ? `${source}, ${label} ${updated}` : source

  return <p className={styles.source}>{text}</p>
}
